import { Head, Link } from "@inertiajs/react"
import { ThumbsDown, ThumbsUp } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { cn } from "@/lib/utils"

type RatedQuestion = {
  id: number
  text: string
  source: string
  href: string | null
  helpful: number
  unhelpful: number
  comments: string[]
}

export default function AdminQuestionRatingsIndex({
  questions,
  filter,
}: {
  questions: RatedQuestion[]
  filter: "all" | "unhelpful"
}) {
  const filters = [
    { value: "all", label: "All rated", href: "/admin/question_ratings" },
    { value: "unhelpful", label: "Has “not helpful” votes", href: "/admin/question_ratings?filter=unhelpful" },
  ] as const

  return (
    <>
      <Head title="Explanation ratings">
        <meta name="description" content="See which quiz and exam explanations users found helpful or not." />
        <meta property="og:title" content="Explanation ratings" />
        <meta property="og:description" content="See which quiz and exam explanations users found helpful or not." />
      </Head>
      <AdminShell>
        <PageHeader
          title="Explanation ratings"
          description="How users rated the explanations after answering. Questions with the most “not helpful” votes come first."
          tabs={
            <nav aria-label="Filter" className="flex flex-wrap items-end gap-6 border-b border-hairline">
              {filters.map((item) => (
                <Link
                  key={item.value}
                  href={item.href}
                  aria-current={item.value === filter ? "page" : undefined}
                  className={cn(
                    "-mb-px border-b-2 px-1 py-3 text-sm no-underline",
                    item.value === filter
                      ? "border-accent font-medium text-accent-display"
                      : "border-transparent text-ink-body hover:text-ink-display",
                  )}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
          }
        />

        {questions.length === 0 ? (
          <div className="callout mt-6">
            <p>No explanation ratings yet.</p>
          </div>
        ) : (
          <ul className="mt-6 divide-y divide-hairline overflow-hidden rounded-xl border border-hairline bg-page">
            {questions.map((question) => (
              <li key={question.id} className="px-4 py-3">
                <div className="flex items-start gap-4">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-ink-display">{question.text}</p>
                    <p className="mt-1 text-xs text-ink-muted">
                      {question.href ? (
                        <Link href={question.href}>{question.source}</Link>
                      ) : (
                        question.source
                      )}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-4 text-sm">
                    <span className="inline-flex items-center gap-1 text-accent-display" title="Helpful">
                      <ThumbsUp className="h-4 w-4" aria-hidden="true" /> {question.helpful}
                      <span className="sr-only">found it helpful</span>
                    </span>
                    <span className="inline-flex items-center gap-1 text-danger-display" title="Not helpful">
                      <ThumbsDown className="h-4 w-4" aria-hidden="true" /> {question.unhelpful}
                      <span className="sr-only">found it not helpful</span>
                    </span>
                  </div>
                </div>
                {question.comments.length > 0 && (
                  <ul className="mt-2 space-y-1 border-l border-hairline pl-3">
                    {question.comments.map((comment, index) => (
                      <li key={index} className="whitespace-pre-wrap text-xs text-ink-body">
                        “{comment}”
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        )}
      </AdminShell>
    </>
  )
}
